import { MultiSelectOption } from './CommonTypes';
import {
  AssignmentCategory,
  FNOL_TaskTypes,
  LossLocation,
  Severity,
} from './fnol';
import type { ClaimAttributeForm } from './fnol';
import { PropertyTypeEnum } from './task';
import type { SupplementalData } from './task';
import * as yup from 'yup';
import { PriorityLevel } from './task-template';
import { sub } from 'date-fns';
import { isEmpty, isNumber } from 'lodash';
import { buildPinnedClaimAttributesSchema } from './pinnedClaimAttributesSchema';
import { buildIngressDateOfLossSchema } from './dateOfLossSchema';
import { buildReferralDetailsSchema } from './referralDetailsSchema';

export interface IngressRequest {
  $msg: {
    externalId: string; //guid we create
    claim: {
      lossLocation: LossLocation;
      jobId?: string;
      taskId?: string;
      claimNumber: string;
      insuredName: string;
      catCode?: string;
      propertyType: PropertyTypeEnum;
      dateOfLoss: string;
      dateOfNotice?: string;
      peril?: string;
      sourcedFrom?: string;
      isModularHome?: boolean;
      jobNumber?: string;
      supplementalData?: SupplementalData[];
    };
    contact: {
      primary: IngressContact;
      secondary?: IngressContact;
      altLocation?: LossLocation;
    };
    assignmentHandling: {
      travel: boolean;
      templateId?: string;
      durationInMinutes?: number;
      priority?: string;
      severity?: Severity;
      methodOfInspection?: string;
      mustEndBefore: string; // Date Time
      mustStartAfter: string; // Date Time
      notes?: string;
      preferredLanguage?: string;
      assignmentCategory: AssignmentCategory;
      assignmentType?: AssignmentType;
      inspectionType?: InspectionType;
    };
    requiredResource?: {
      name: string;
      id: string;
    };
    taskType?: FNOL_TaskTypes;
  };
  requiredAttributes?: {
    [key: string]: string | string[];
  };
}

export interface IngressRequestForm {
  claim: {
    lossLocation: LossLocation;
    jobId?: string;
    taskId?: string;
    claimNumber: string;
    insuredName: string;
    catCode?: string;
    propertyType: PropertyTypeEnum;
    dateOfLoss: string;
    dateOfNotice?: string;
    peril?: string;
    sourcedFrom?: string;
    isModularHome?: boolean;
    jobNumber?: string;
    isForced?: boolean;
  };
  contact: {
    primary: IngressContact;
    secondary?: IngressContact;
    altLocation?: LossLocation;
    hasAlternateLocation: boolean;
    hasSecondaryContact: boolean;
  };
  assignmentHandling: {
    travel: boolean;
    templateId?: string;
    durationInMinutes?: number;
    priority?: string;
    severity?: Severity;
    methodOfInspection?: string;
    mustEndBefore: string;
    mustStartAfter: string;
    notes?: string;
    preferredLanguage?: string;
    assignmentCategory: AssignmentCategory;
    assignmentType?: AssignmentType;
    inspectionType?: InspectionType;
    taskType: FNOL_TaskTypes;
  };
  requiredResource?: MultiSelectOption;
  claimAttributes: ClaimAttributeForm[];
  timeZone?: string;
  additionalData?: AdditionalData;
}

export interface AdditionalData {
  referrersPhone?: string;
  lineOfBusiness?: string;
  exposureNumber?: string;
  strategicBusinessUnit?: string;
  claimSystem?: string;
  scopeOfWork?: string;
  typeOfReport?: string;
  budget?: string;
  fileInLitigation?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleYear?: string;
  vehicleVinOrSerial?: string;
}

export const ExposureNumberOptions: MultiSelectOption[] = Array.from(
  { length: 9 },
  (_, index) => {
    const value = `000${index + 1}`;

    return { title: value, value };
  }
);

export const PriorityLevelOptions: MultiSelectOption[] = [
  { title: 'Low', value: PriorityLevel.LOW },
  { title: 'Medium', value: PriorityLevel.MEDIUM },
  { title: 'High', value: PriorityLevel.HIGH },
];

export interface IngressAttributes {
  name: string;
  isCat: boolean;
  preferrence?: IngressAttributePreferrence;
}

export enum IngressAttributePreferrence { // Following the spelling in APIM
  REQUIRED = 'required',
  PREFERRED = 'preferred',
}
export enum InspectionType {
  INSPECTION_ONLY = 'inspectOnly',
  SETTLE = 'settle',
}

export const InspectionTypeOptions: MultiSelectOption[] = [
  {
    title: 'Inspection Only',
    value: InspectionType.INSPECTION_ONLY,
  },
  {
    title: 'Settle',
    value: InspectionType.SETTLE,
  },
];

export enum AssignmentType {
  PROPERTY_STRUCTURE = 'propertyStructure',
  PROPERTY_CONTENTS = 'propertyContents',
}

export const AssignmentTypeOptions: MultiSelectOption[] = [
  {
    title: 'Property Structure',
    value: AssignmentType.PROPERTY_STRUCTURE,
  },
  {
    title: 'Property Contents',
    value: AssignmentType.PROPERTY_CONTENTS,
  },
];

export interface IngressContact {
  name: string;
  phone: string;
  email?: string;
  contactMethod: string;
  phone2: string;
}

export enum IngressContactMethod {
  PHONE = 'phone',
  EMAIL = 'email',
}

export const IngressContactMethodOptions: MultiSelectOption[] = [
  { title: 'Phone', value: IngressContactMethod.PHONE },
  { title: 'Email', value: IngressContactMethod.EMAIL },
];

export const IngressRequestFormSchema = yup.object({
  claim: yup.object({
    lossLocation: yup.object({
      streetLine1: yup.string().nullable(),
      streetLine2: yup.string().nullable(),
      city: yup.string().required(),
      state: yup.string().required(),
      zip: yup.string().nullable(),
    }),
    jobId: yup.string().nullable(),
    taskId: yup.string().nullable(),
    claimNumber: yup
      .string()
      .required()
      .matches(
        /^[\S]{5,40}$/,
        'Claim number must be between 5 and 40 characters long and no spaces'
      ),
    insuredName: yup.string().required(),
    catCode: yup.string().nullable(),
    propertyType: yup.string().required(),
    dateOfLoss: yup.string().required(),
    dateOfNotice: yup.string().nullable(),
    peril: yup.string().nullable().when('isForced', {
      is: true,
      then: yup.string().required(),
    }),
    sourcedFrom: yup.string().nullable(),
    isModularHome: yup.boolean().nullable(),
    jobNumber: yup.string().nullable(),
  }),
  contact: yup.object({
    primary: yup
      .object({
        name: yup.string().required(),
        phone: yup.string().nullable(),
        email: yup.string().email().nullable(),
        contactMethod: yup.string().nullable(),
        phone2: yup.string().nullable(),
      })
      .required(),
    secondary: yup
      .object({
        name: yup.string().nullable(),
        phone: yup.string().nullable(),
        email: yup.string().email().nullable(),
        contactMethod: yup.string().nullable(),
        phone2: yup.string().nullable(),
      })
      .nullable()
      .when('hasSecondaryContact', {
        is: true,
        then: yup
          .object({
            name: yup.string().required(),
            phone: yup.string().nullable(),
            email: yup.string().email().nullable(),
            contactMethod: yup.string().nullable(),
            phone2: yup.string().nullable(),
          })
          .required(),
      }),
    altLocation: yup
      .object({
        streetLine1: yup.string().nullable(),
        streetLine2: yup.string().nullable(),
        city: yup.string().nullable(),
        state: yup.string().nullable(),
        zip: yup.string().nullable(),
      })
      .nullable()
      .when('hasAlternateLocation', {
        is: true,
        then: yup
          .object({
            streetLine1: yup.string().required(),
            streetLine2: yup.string().nullable(),
            city: yup.string().required(),
            state: yup.string().required(),
            zip: yup.string().required(),
          })
          .required(),
      }),
  }),
  assignmentHandling: yup.object({
    travel: yup
      .boolean()
      .required()
      .when('methodOfInspection', {
        is: (val: string) => val === 'FIELD',
        then: yup
          .boolean()
          .test(
            'is-travel-required',
            'Travel is required when method of inspection is field',
            (value) => value === true
          ),
      }),
    templateId: yup.string().nullable(),
    durationInMinutes: yup
      .number()
      .transform((_, val) => {
        if (isNumber(val)) {
          return val;
        }

        if (isEmpty(val)) {
          return null;
        }

        return Number(val);
      })
      .nullable()
      .min(0)
      .max(360)
      .when('fnolTaskType', {
        is: (val: FNOL_TaskTypes) => val === FNOL_TaskTypes.NON_COMPLEX,
        then: yup
          .number()
          .transform((_, val) => {
            if (isNumber(val)) {
              return val;
            }

            if (isEmpty(val)) {
              return null;
            }

            return Number(val);
          })
          .nullable()
          .required()
          .min(0)
          .max(360),
      }),
    priority: yup.string().nullable(),
    severity: yup.string().nullable(),
    methodOfInspection: yup
      .string()
      .nullable()
      .when('taskType', {
        is: (val: FNOL_TaskTypes) => val === FNOL_TaskTypes.NON_COMPLEX,
        then: yup.string().required(),
      }),
    mustEndBefore: yup
      .string()
      .test(
        'min_end_date',
        'End date time must be the same or after start date time',
        (value, context) => {
          const { mustStartAfter } = context.parent;

          if (!mustStartAfter || !value) {
            return false;
          }

          return value >= mustStartAfter;
        }
      ),
    mustStartAfter: yup.string().required(),
    notes: yup.string().nullable(),
    preferredLanguage: yup.string().nullable(),
    assignmentCategory: yup.string().required(),
    assignmentType: yup.string().nullable(),
    inspectionType: yup.string().nullable(),
    taskType: yup.string().required(),
  }),
  requiredResource: yup
    .object()
    .nullable()
    .when('claim.isForced', {
      is: true,
      then: yup
        .object()
        .transform((value) => {
          if (!value) {
            value = undefined;
          }

          return value;
        })
        .required('Resource is required when task is forced'),
    }),
  timeZone: yup
    .string()
    .nullable()
    .when('claim.isForced', {
      is: true,
      then: yup.string().required('Time zone is required when task is forced'),
    }),
});

export type QualifiedIngressRequestFormSchemaOptions = {
  pinnedGuids: readonly string[];
};

/**
 * The qualified create flow's Claim Information schema: IngressRequestFormSchema
 * plus a non-empty value on every pinned claim attribute row, a named message
 * for a missing Date of Loss (the flow has no made-up default), and the
 * required billing details. The shared schema is left untouched because the
 * sibling create flow uses it.
 */
export const buildQualifiedIngressRequestFormSchema = (
  options: QualifiedIngressRequestFormSchemaOptions
): yup.AnyObjectSchema =>
  IngressRequestFormSchema.shape({
    claim: IngressRequestFormSchema.fields.claim.shape({
      dateOfLoss: buildIngressDateOfLossSchema(),
    }),
    claimAttributes: buildPinnedClaimAttributesSchema(options.pinnedGuids),
    additionalData: buildReferralDetailsSchema(),
  });

export const IngressRequestDefaultValues: IngressRequestForm = {
  claim: {
    lossLocation: {
      streetLine1: '',
      city: '',
      state: '',
      zip: '',
    },
    claimNumber: '',
    insuredName: '',
    propertyType: PropertyTypeEnum.RESIDENTIAL,
    dateOfLoss: sub(new Date(), {
      days: 5,
    }).toISOString(),
    dateOfNotice: new Date().toISOString(),
  },
  contact: {
    primary: {
      name: '',
      phone: '',
      email: '',
      contactMethod: IngressContactMethod.EMAIL,
      phone2: '',
    },
    hasAlternateLocation: false,
    hasSecondaryContact: false,
  },
  assignmentHandling: {
    travel: true,
    durationInMinutes: 90,
    severity: Severity.TWO,
    mustEndBefore: new Date().toISOString(),
    mustStartAfter: new Date().toISOString(),
    priority: PriorityLevel.MEDIUM,
    assignmentCategory: AssignmentCategory.INITIAL,
    taskType: FNOL_TaskTypes.NON_COMPLEX,
  },
  claimAttributes: [],
  requiredResource: {
    title: '',
    value: '',
  },
};

export interface CWSLossAddress {
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  zipCode: string;
  timezone: string;
}

export interface CWSClaimContact {
  firstName: string;
  lastName: string;
  primaryPhone: string | null;
  alternatePhone: string | null;
  email: string | null;
}

export interface CWSSummary {
  uniqueId: string;
  claimNumber: string;
  peril: string;
  catastropheNumber: string | null;
  propertyTypes: string[];
  lossAddress: CWSLossAddress;
  claimContact: CWSClaimContact;
  lossDate: string;
  creationDate: string;
}

export interface CWSSummaryResponse {
  summary: CWSSummary;
}

export interface QualifiedResourcesFNOLFilters {
  pageSize?: number;
  lossLocation?: {
    streetLine1: string;
    streetLine2?: string;
    city: string;
    state: string;
    zip: string;
  };
  methodOfInspection?: string;
  catCode?: string;
  severity: string;
  peril?: string;
  startTime: string;
  endTime: string;
  durationInMinutes?: number;
  preferredLanguage?: string;
  assignmentCategory: string;
  requiredAttributes?: {
    [attributeDefinitionIntegrationGUID: string]: string[];
  };
  resourceIds?: string[];
}
